/**
 * Signup Modal for @ollaid/native-sso — Design aligned with web SSO
 * Full signup flow: intro → account-type → info → OTP → password → confirm → success
 * 
 * @version 2.7.9
 */

import { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody, DialogFooter,
  Button, Input, Label,
  IconShieldCheck, IconCheckCircle2, IconMail, IconSmartphone, IconArrowLeft,
  IconKeyRound, IconLock, IconLoader2,
  IconHome, IconCalendar, IconBell, IconMessageCircle, IconUsers, IconSettings,
  IconLink,
} from './ui';
import PhoneInput from './PhoneInput';
import OTPInput from './OTPInput';
import AppsLogoSlider from './AppsLogoSlider';
import PasswordRecoveryModal from './PasswordRecoveryModal';
import { useMobileRegistration } from '../hooks/useMobileRegistration';
import { nativeAuthService } from '../services/nativeAuth';
import { COUNTRIES_SORTED_BY_CODE, getCountryByCode, DEFAULT_COUNTRY_CODE } from '../utils/countries';
import type { UserInfos } from '../types/native';
import type { MobileRegistrationFormData } from '../types/mobile';
import packageFavicon from '../logo/favicon.png';

type SignupStep = 'intro' | 'account-type' | 'info' | 'otp' | 'password' | 'confirm';

const SSO_VERSION = '2.8.2';

export interface SignupModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSwitchToLogin: () => void;
  onSignupSuccess: (token: string, user: UserInfos) => void;
  saasApiUrl: string;
  iamApiUrl: string;
  logoUrl?: string;
  /** Type de compte par défaut à persister dans le storage du package */
  defaultAccountType?: 'user' | 'client';
  homeUrl?: string;
  /** Called when conflict resolution wants to switch to login with a pre-filled phone */
  onSwitchToLoginWithPhone?: (phone: string) => void;
}

const C = {
  primary: '#002147',
  accent: '#e8430a',
  green: '#16a34a',
  greenBg: '#dcfce7',
  gray100: '#f3f4f6',
  gray200: '#e5e7eb',
  gray500: '#6b7280',
  gray700: '#374151',
  gray900: '#111827',
  red: '#dc2626',
  redBg: '#fef2f2',
  amber: '#f59e0b',
  amberBg: '#fef3c7',
  white: '#ffffff',
} as const;

const iconCircle = (bg: string, size = '3rem'): React.CSSProperties => ({
  margin: '0 auto', width: size, height: size, backgroundColor: bg, borderRadius: '50%',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
});

const brandBadgeCircle = (bg: string): React.CSSProperties => ({
  width: '2.9rem',
  height: '2.9rem',
  borderRadius: '9999px',
  backgroundColor: bg,
  border: `1px solid ${C.gray200}`,
  boxShadow: '0 8px 18px rgba(0, 0, 0, 0.08)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0.5rem',
  boxSizing: 'border-box',
  overflow: 'hidden',
  flexShrink: 0,
});

const brandLinkPill: React.CSSProperties = {
  width: '2.1rem',
  height: '2.1rem',
  borderRadius: '9999px',
  backgroundColor: C.gray100,
  border: `1px solid ${C.gray200}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  boxShadow: '0 4px 10px rgba(0, 0, 0, 0.05)',
};

const brandRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.65rem',
  marginBottom: '0.9rem',
};

const backBtnStyle: React.CSSProperties = {
  position: 'absolute', left: '1rem', top: '1rem', background: 'none', border: 'none',
  cursor: 'pointer', padding: '0.5rem', borderRadius: '0.375rem', color: C.gray700, zIndex: 10,
};

const linkStyle: React.CSSProperties = {
  color: C.primary, cursor: 'pointer', background: 'none', border: 'none',
  fontWeight: 500, textDecoration: 'underline', fontSize: '0.875rem',
};

const TOTAL_STEPS = 6;

function StepIndicator({ current }: { current: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginTop: '0.75rem' }}>
      {Array.from({ length: TOTAL_STEPS }, (_, i) => (
        <div key={i} style={{
          width: i + 1 === current ? '1.5rem' : '0.5rem',
          height: '0.5rem',
          borderRadius: '9999px',
          backgroundColor: i + 1 <= current ? C.accent : C.gray200,
          transition: 'all 0.2s',
        }} />
      ))}
      <span style={{ fontSize: '0.75rem', color: C.gray500, marginLeft: '0.5rem' }}>Étape {current}/{TOTAL_STEPS}</span>
    </div>
  );
}

// Orbit success animation
function SuccessOrbit() {
  const orbitIcons = [IconHome, IconCalendar, IconBell, IconMessageCircle, IconUsers, IconSettings];
  return (
    <div style={{ position: 'relative', width: '10rem', height: '10rem', margin: '0 auto' }}>
      {/* Center brand link */}
      <div style={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: '4rem', height: '4rem', backgroundColor: C.greenBg, borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2,
      }}>
        <IconLink style={{ width: '1.7rem', height: '1.7rem', color: C.green }} />
      </div>
      {/* Orbiting icons */}
      {orbitIcons.map((Icon, i) => {
        const angle = (i * 360) / orbitIcons.length;
        return (
          <div key={i} style={{
            position: 'absolute', top: '50%', left: '50%', width: '2rem', height: '2rem',
            transform: `rotate(${angle}deg) translate(4rem) rotate(-${angle}deg)`,
            transformOrigin: '0 0',
            animation: `spin ${8 + i}s linear infinite`,
          }}>
            <div style={{
              width: '2rem', height: '2rem', backgroundColor: C.gray100, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Icon style={{ width: '1rem', height: '1rem', color: C.gray500 }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function SignupModal({ open, onOpenChange, onSwitchToLogin, onSignupSuccess, saasApiUrl, iamApiUrl, logoUrl, defaultAccountType, homeUrl, onSwitchToLoginWithPhone }: SignupModalProps) {
  const {
    status, formData, loading: regLoading, error: regError, conflict, accountType, setAccountType, isPhoneOnly, otpMethod, otpSentTo,
    updateFormData, initRegistration, verifyOtp, completeRegistration, resendOtp, reset: resetReg, clearError,
  } = useMobileRegistration({ saasApiUrl, iamApiUrl });

  const [showPasswordRecovery, setShowPasswordRecovery] = useState(false);

  const [step, setStep] = useState<SignupStep>('intro');
  const [otpCode, setOtpCode] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [signupData, setSignupData] = useState<{ token: string; user: UserInfos } | null>(null);

  const error = localError || regError;
  const primaryLogo = logoUrl || null;
  const showHomeLink = Boolean(homeUrl);

  useEffect(() => { if (!open) { setStep('intro'); setOtpCode(''); setPassword(''); setPasswordConfirm(''); setLocalError(null); setSignupSuccess(false); setSignupData(null); setResendCooldown(0); resetReg(); } }, [open, resetReg]);
  useEffect(() => { if (status === 'pending_otp') { setStep('otp'); setResendCooldown(60); } else if (status === 'pending_password') setStep('password'); }, [status]);
  useEffect(() => { if (resendCooldown > 0) { const t = setTimeout(() => setResendCooldown(r => r - 1), 1000); return () => clearTimeout(t); } }, [resendCooldown]);
  useEffect(() => { if (signupSuccess && signupData) { const t = setTimeout(() => onSignupSuccess(signupData.token, signupData.user), 2000); return () => clearTimeout(t); } }, [signupSuccess, signupData, onSignupSuccess]);

  const exchangeCallbackToken = async (callbackToken: string) => {
    try {
      const response = await nativeAuthService.exchange(callbackToken);
      if (response.iam_token && response.reference && response.alias_reference) {
        setSignupData({
          token: response.iam_token,
          user: {
            ...response.user_infos,
            reference: response.reference,
            alias_reference: response.alias_reference,
          } as UserInfos,
        });
        setSignupSuccess(true);
      } else setLocalError("Erreur lors de la finalisation de l'inscription");
    } catch { setLocalError('Erreur de connexion au serveur'); }
  };

  const handleInfoSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setLocalError(null); clearError();
    if (!formData.name?.trim()) { setLocalError('Le nom est requis'); return; }
    if (!isPhoneOnly && !formData.email?.trim()) { setLocalError("L'adresse email est requise"); return; }
    if (!formData.phone?.trim() || (formData.phone?.length || 0) < 6) { setLocalError('Numéro de téléphone invalide'); return; }
    if (formData.ccphone === '+221' && formData.phone?.length !== 9) { setLocalError('Le numéro sénégalais doit contenir exactement 9 chiffres'); return; }
    if (isPhoneOnly && formData.ccphone !== '+221') { setLocalError("L'inscription par téléphone est réservée aux numéros sénégalais (+221)"); return; }
    if (!formData.town?.trim()) { setLocalError('La ville est requise'); return; }
    if (!formData.country?.trim()) { setLocalError('Le pays est requis'); return; }
    const registrationData: Omit<MobileRegistrationFormData, 'password' | 'password_confirmation'> = {
      name: formData.name || '',
      email: formData.email || '',
      ccphone: formData.ccphone || '+221',
      phone: formData.phone || '',
      town: formData.town,
      country: formData.country,
    };
    await initRegistration(registrationData);
  };

  const handleOTPSubmit = async () => {
    if (otpCode.length !== 6) { setLocalError('Veuillez entrer le code à 6 chiffres'); return; }
    setLocalError(null); clearError();
    const result = await verifyOtp(otpCode);
    if (result.success && result.completed && result.callback_token) await exchangeCallbackToken(result.callback_token);
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault(); setLocalError(null);
    if (!password || password.length < 8) { setLocalError('Le mot de passe doit contenir au moins 8 caractères'); return; }
    if (password !== passwordConfirm) { setLocalError('Les mots de passe ne correspondent pas'); return; }
    setStep('confirm');
  };

  const handleConfirm = async () => {
    setLocalError(null); clearError();
    const result = await completeRegistration(password);
    if (result.success && result.callback_token) await exchangeCallbackToken(result.callback_token);
  };

  const handleResendOTP = async () => { if (resendCooldown > 0) return; const r = await resendOtp(); if (r.success) setResendCooldown(r.cooldown || 60); };

  const goToStep = (s: SignupStep) => { setLocalError(null); clearError(); setStep(s); };

  const handleAccountTypeSelect = (type: 'email' | 'phone-only') => {
    setAccountType(type);
    if (type === 'phone-only') updateFormData({ ccphone: '+221', country: 'SN', email: '' });
    else if (!formData.country) updateFormData({ country: DEFAULT_COUNTRY_CODE });
    goToStep('info');
  };

  const stepNumber = { intro: 1, 'account-type': 2, info: 3, otp: 4, password: 5, confirm: 6 }[step];

  const renderError = () => error ? <div style={{ padding: '0.75rem', borderRadius: '0.375rem', backgroundColor: C.redBg, color: C.red, fontSize: '0.875rem' }}>{error}</div> : null;
  const getOtpDeliveryLabel = (method?: 'email' | 'sms' | null, sentTo?: string | null) => {
    if (method === 'sms') {
      return sentTo ? `par SMS au ${sentTo}` : 'par SMS';
    }
    if (method === 'email') {
      return sentTo ? `par email à ${sentTo}` : 'par email';
    }

    return accountType === 'phone-only'
      ? `par SMS au ${formData.ccphone || '+221'} ${formData.phone || ''}`.trim()
      : formData.email
        ? `par email à ${formData.email}`
        : 'pour vérification';
  };
  const BrandHeader = ({ color = C.primary }: { color?: string }) => (
    <div style={brandRowStyle}>
      <div style={brandBadgeCircle(C.white)}>
        {primaryLogo ? (
          <img src={primaryLogo} alt="Application" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <IconShieldCheck style={{ width: '1.4rem', height: '1.4rem', color }} />
        )}
      </div>
      <div style={brandLinkPill} aria-hidden="true">
        <IconLink style={{ width: '1rem', height: '1rem', color: C.gray500 }} />
      </div>
      <div style={brandBadgeCircle(C.white)}>
        <img src={packageFavicon} alt="Ollaid SSO" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
      </div>
    </div>
  );

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>

        {/* ===================== SUCCESS ===================== */}
        {signupSuccess && signupData ? (
          <DialogHeader style={{ textAlign: 'center' }}>
            <SuccessOrbit />
            <DialogTitle>Félicitations !</DialogTitle>
            <DialogDescription>
              Votre compte a été créé avec succès. Bienvenue {formData.name} !
            </DialogDescription>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', fontSize: '0.875rem', color: C.gray500, marginTop: '1rem' }}>
              <IconLoader2 style={{ width: '1rem', height: '1rem' }} />
              Connexion automatique en cours...
            </div>
          </DialogHeader>
        ) : (
          <>
            {/* ===== INTRO ===== */}
            {step === 'intro' && (
              <>
                <DialogHeader style={{ textAlign: 'center' }}>
                  <BrandHeader color={C.accent} />
                  <DialogTitle>Ouvrez un compte Ollaid</DialogTitle>
                  <DialogDescription>Un compte unique qui vous donne accès à toutes les applications</DialogDescription>
                </DialogHeader>
                <DialogBody>
                  <AppsLogoSlider iamApiUrl={iamApiUrl} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', fontSize: '0.875rem', color: C.gray500, margin: '0.75rem 0' }}>
                    {['Un seul compte pour toutes les applications', 'Plus besoin de multiples mots de passe', 'Connexion simplifiée et sécurisée'].map(text => (
                      <div key={text} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <IconCheckCircle2 style={{ width: '1.25rem', height: '1.25rem', color: C.green, flexShrink: 0 }} />
                        {text}
                      </div>
                    ))}
                  </div>
                  <StepIndicator current={1} />
                </DialogBody>
                <DialogFooter>
                  <Button onClick={() => goToStep('account-type')} style={{ width: '100%' }}>Suivant →</Button>
                  <div style={{ textAlign: 'center', fontSize: '0.875rem' }}>
                    <span style={{ color: C.gray500 }}>Déjà un compte ? </span>
                    <button type="button" style={linkStyle} onClick={onSwitchToLogin}>Connectez-vous</button>
                  </div>
                  {showHomeLink && (
                    <div style={{ textAlign: 'center', fontSize: '0.875rem' }}>
                      <button
                        type="button"
                        style={linkStyle}
                        onClick={() => { if (homeUrl) window.location.href = homeUrl; }}
                      >
                        Retourner à l'accueil
                      </button>
                    </div>
                  )}
                  <div style={{ textAlign: 'center', fontSize: '0.72rem', color: C.gray500, paddingTop: '0.25rem' }}>
                    OLLAID SSO v{SSO_VERSION} · © 2026 OLLAID
                  </div>
                </DialogFooter>
              </>
            )}

            {/* ===== ACCOUNT TYPE ===== */}
            {step === 'account-type' && (
              <>
                <button onClick={() => goToStep('intro')} style={backBtnStyle} type="button">
                  <IconArrowLeft style={{ width: '1rem', height: '1rem' }} />
                </button>
                <DialogHeader style={{ textAlign: 'center' }}>
                  <BrandHeader color={C.primary} />
                  <DialogTitle>Choisissez votre type de compte</DialogTitle>
                </DialogHeader>
                <DialogBody>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <button type="button" onClick={() => handleAccountTypeSelect('email')}
                      style={{ width: '100%', padding: '1rem', border: `2px solid ${C.gray200}`, borderRadius: '0.5rem', textAlign: 'left', cursor: 'pointer', background: C.white, display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div style={{ width: '2.5rem', height: '2.5rem', backgroundColor: C.accent, borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <IconMail style={{ width: '1.25rem', height: '1.25rem', color: C.white }} />
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, color: C.gray900 }}>Email + Téléphone</div>
                        <div style={{ fontSize: '0.875rem', color: C.gray500 }}>Compte complet</div>
                      </div>
                    </button>
                    <button type="button" onClick={() => handleAccountTypeSelect('phone-only')}
                      style={{ width: '100%', padding: '1rem', border: `2px solid ${C.gray200}`, borderRadius: '0.5rem', textAlign: 'left', cursor: 'pointer', background: C.white, display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div style={{ width: '2.5rem', height: '2.5rem', backgroundColor: C.accent, borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <IconSmartphone style={{ width: '1.25rem', height: '1.25rem', color: C.white }} />
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, color: C.gray900 }}>Téléphone uniquement</div>
                        <div style={{ fontSize: '0.875rem', color: C.gray500 }}>🇸🇳 Sénégal uniquement</div>
                      </div>
                    </button>
                  </div>
                  <StepIndicator current={2} />
                </DialogBody>
              </>
            )}

            {/* ===== INFO (with conflict resolution) ===== */}
            {step === 'info' && (
              <>
                <button onClick={() => goToStep('account-type')} style={backBtnStyle} type="button">
                  <IconArrowLeft style={{ width: '1rem', height: '1rem' }} />
                </button>

                {/* ---- CONFLICT VIEW ---- */}
                {conflict ? (
                  <>
                    <DialogHeader style={{ textAlign: 'center' }}>
                      <BrandHeader color={C.amber} />
                      <DialogTitle>
                        {conflict.type === 'phone' ? 'Numéro déjà utilisé' : 'Email déjà utilisé'}
                      </DialogTitle>
                      <DialogDescription>
                        {conflict.type === 'phone'
                          ? `Le numéro ${formData.ccphone || ''} ${formData.phone || ''} est déjà associé à un compte.`
                          : `L'adresse ${formData.email || ''} est déjà associée à un compte.`}
                        {' '}S'il s'agit du vôtre, connectez-vous.
                      </DialogDescription>
                    </DialogHeader>
                    <DialogFooter style={{ borderTop: 'none' }}>
                      {conflict.type === 'phone' && formData.ccphone === '+221' && conflict.options?.can_login && (
                        <Button
                          onClick={() => {
                            const fullPhone = formData.phone || '';
                            if (onSwitchToLoginWithPhone) onSwitchToLoginWithPhone(fullPhone);
                            else onSwitchToLogin();
                          }}
                          style={{ width: '100%' }}
                        >
                          <IconSmartphone style={{ width: '1rem', height: '1rem' }} />
                          Se connecter par téléphone
                        </Button>
                      )}
                      {(conflict.type === 'email' || (conflict.type === 'phone' && formData.ccphone !== '+221')) && conflict.options?.can_login && (
                        <Button onClick={onSwitchToLogin} style={{ width: '100%' }}>
                          Se connecter
                        </Button>
                      )}
                      {!isPhoneOnly && (conflict.options?.can_recover_by_email || conflict.options?.can_recover_by_sms) && (
                        <Button variant="outline" onClick={() => setShowPasswordRecovery(true)} style={{ width: '100%' }}>
                          Récupérer mon compte
                        </Button>
                      )}
                      <Button variant="outline" onClick={() => clearError()} style={{ width: '100%' }}>
                        {conflict.type === 'phone' ? 'Changer le numéro' : "Changer l'email"}
                      </Button>
                      {conflict.options?.masked_email && (
                        <div style={{ padding: '0.75rem', backgroundColor: C.gray100, borderRadius: '0.375rem', fontSize: '0.8125rem', color: C.gray500, textAlign: 'center' }}>
                          Email lié : <strong style={{ color: C.gray900 }}>{conflict.options.masked_email}</strong>
                        </div>
                      )}
                      {conflict.options?.masked_phone && (
                        <div style={{ padding: '0.75rem', backgroundColor: C.gray100, borderRadius: '0.375rem', fontSize: '0.8125rem', color: C.gray500, textAlign: 'center' }}>
                          Téléphone lié : <strong style={{ color: C.gray900 }}>{conflict.options.masked_phone}</strong>
                        </div>
                      )}
                    </DialogFooter>
                  </>
                ) : (
                  /* ---- NORMAL INFO FORM ---- */
                  <>
                    <DialogHeader style={{ textAlign: 'center' }}>
                      <BrandHeader color={C.primary} />
                      <DialogTitle>Créer votre compte</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleInfoSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                      <DialogBody>
                        {isPhoneOnly && (
                          <div style={{ padding: '0.75rem', borderRadius: '0.375rem', backgroundColor: C.accent + '1a', color: C.accent, fontSize: '0.8125rem', marginBottom: '0.5rem', textAlign: 'center' }}>
                            📱 Inscription par téléphone — 🇸🇳 Sénégal uniquement
                          </div>
                        )}
                        {!isPhoneOnly && (
                          <div style={{ padding: '0.75rem', borderRadius: '0.375rem', backgroundColor: C.amberBg, color: '#92400e', fontSize: '0.8125rem', marginBottom: '0.5rem', textAlign: 'center' }}>
                            ⚠️ Un code OTP sera envoyé pour vérification
                          </div>
                        )}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          {renderError()}
                          <div><Label>Nom complet <span style={{ color: C.red }}>*</span></Label><Input placeholder="Jean Dupont" value={formData.name || ''} onChange={e => updateFormData({ name: e.target.value })} disabled={regLoading} /></div>
                          {!isPhoneOnly && <div><Label>Adresse email <span style={{ color: C.red }}>*</span></Label><Input type="email" placeholder="vous@exemple.com" value={formData.email || ''} onChange={e => updateFormData({ email: e.target.value })} disabled={regLoading} /></div>}
                          <div><Label>Numéro de téléphone <span style={{ color: C.red }}>*</span></Label><PhoneInput value={formData.phone || ''} onChange={p => updateFormData({ phone: p })} ccphone={formData.ccphone || '+221'} onCcphoneChange={c => updateFormData({ ccphone: c })} disabled={regLoading} lockCcphone={isPhoneOnly} /></div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                            <div><Label>Ville <span style={{ color: C.red }}>*</span></Label><Input placeholder="Dakar" value={formData.town || ''} onChange={e => updateFormData({ town: e.target.value })} disabled={regLoading} /></div>
                            <div>
                              <Label>Pays <span style={{ color: C.red }}>*</span></Label>
                              <select
                                value={formData.country || DEFAULT_COUNTRY_CODE}
                                onChange={e => {
                                  const country = getCountryByCode(e.target.value);
                                  updateFormData({ country: e.target.value, ccphone: country?.dialCode || '+221' });
                                }}
                                disabled={regLoading || isPhoneOnly}
                                style={{ width: '100%', padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #d1d5db', backgroundColor: (regLoading || isPhoneOnly) ? '#f3f4f6' : 'white', fontSize: '0.875rem' }}
                              >
                                {COUNTRIES_SORTED_BY_CODE.map(c => (
                                  <option key={c.code} value={c.code}>{c.code} {c.flag} {c.name}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <StepIndicator current={3} />
                        </div>
                      </DialogBody>
                      <DialogFooter>
                        <Button type="submit" disabled={regLoading} style={{ width: '100%' }}>
                          {regLoading ? (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <IconLoader2 style={{ width: '1rem', height: '1rem' }} /> Vérification...
                            </span>
                          ) : 'Continuer'}
                        </Button>
                      </DialogFooter>
                    </form>
                  </>
                )}
              </>
            )}

            {/* ===== OTP ===== */}
            {step === 'otp' && (
              <>
                <button onClick={() => goToStep('info')} style={backBtnStyle} type="button">
                  <IconArrowLeft style={{ width: '1rem', height: '1rem' }} />
                </button>
                <DialogHeader style={{ textAlign: 'center' }}>
                  <div style={iconCircle(C.accent)}>
                    <IconKeyRound style={{ width: '1.5rem', height: '1.5rem', color: C.white }} />
                  </div>
                  <DialogTitle>Vérification</DialogTitle>
                  <DialogDescription>
                    Entrez le code envoyé {getOtpDeliveryLabel(otpMethod, otpSentTo)}
                  </DialogDescription>
                </DialogHeader>
                <DialogBody>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {renderError()}
                    <OTPInput value={otpCode} onChange={setOtpCode} disabled={regLoading} />
                    <div style={{ textAlign: 'center', fontSize: '0.875rem' }}>
                      {resendCooldown > 0 ? <span style={{ color: C.gray500 }}>Renvoyer dans {resendCooldown}s</span> : <button type="button" onClick={handleResendOTP} style={linkStyle}>Code non reçu ? Renvoyer</button>}
                    </div>
                    <StepIndicator current={4} />
                  </div>
                </DialogBody>
                <DialogFooter>
                  <Button onClick={handleOTPSubmit} disabled={regLoading || otpCode.length !== 6} style={{ width: '100%' }}>
                    {regLoading ? (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <IconLoader2 style={{ width: '1rem', height: '1rem' }} /> Vérification...
                      </span>
                    ) : 'Vérifier'}
                  </Button>
                </DialogFooter>
              </>
            )}

            {/* ===== PASSWORD ===== */}
            {step === 'password' && (
              <>
                <button onClick={() => goToStep('otp')} style={backBtnStyle} type="button">
                  <IconArrowLeft style={{ width: '1rem', height: '1rem' }} />
                </button>
                <DialogHeader style={{ textAlign: 'center' }}>
                  <div style={iconCircle(C.accent)}>
                    <IconLock style={{ width: '1.5rem', height: '1.5rem', color: C.white }} />
                  </div>
                  <DialogTitle>Créer un mot de passe</DialogTitle>
                </DialogHeader>
                <form onSubmit={handlePasswordSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                  <DialogBody>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      {renderError()}
                      <div><Label>Mot de passe</Label><Input type="password" placeholder="Minimum 8 caractères" value={password} onChange={e => setPassword(e.target.value)} disabled={regLoading} /></div>
                      <div><Label>Confirmer le mot de passe</Label><Input type="password" placeholder="••••••••" value={passwordConfirm} onChange={e => setPasswordConfirm(e.target.value)} disabled={regLoading} /></div>
                      <StepIndicator current={5} />
                    </div>
                  </DialogBody>
                  <DialogFooter>
                    <Button type="submit" disabled={regLoading} style={{ width: '100%' }}>Continuer</Button>
                  </DialogFooter>
                </form>
              </>
            )}

            {/* ===== CONFIRM ===== */}
            {step === 'confirm' && (
              <>
                <button onClick={() => goToStep('password')} style={backBtnStyle} type="button">
                  <IconArrowLeft style={{ width: '1rem', height: '1rem' }} />
                </button>
                <DialogHeader style={{ textAlign: 'center' }}>
                  <BrandHeader color={C.primary} />
                  <DialogTitle>Confirmer votre inscription</DialogTitle>
                </DialogHeader>
                <DialogBody>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {renderError()}
                    <div style={{ backgroundColor: C.gray100, borderRadius: '0.5rem', padding: '1rem' }}>
                      {[['Nom', formData.name], ...(!isPhoneOnly ? [['Email', formData.email]] : []), ['Téléphone', `${formData.ccphone} ${formData.phone}`], ['Ville', formData.town], ['Pays', formData.country]].map(([label, value]) => (
                        <div key={label as string} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.375rem 0' }}>
                          <span style={{ fontSize: '0.875rem', color: C.gray500 }}>{label}</span>
                          <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>{value as string}</span>
                        </div>
                      ))}
                    </div>
                    <StepIndicator current={6} />
                  </div>
                </DialogBody>
                <DialogFooter>
                  <Button onClick={handleConfirm} disabled={regLoading} style={{ width: '100%' }}>
                    {regLoading ? (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <IconLoader2 style={{ width: '1rem', height: '1rem' }} /> Création du compte...
                      </span>
                    ) : 'Créer mon compte'}
                  </Button>
                </DialogFooter>
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>

    <PasswordRecoveryModal
      open={showPasswordRecovery}
      onOpenChange={setShowPasswordRecovery}
      onSuccess={() => setShowPasswordRecovery(false)}
      saasApiUrl={saasApiUrl}
      iamApiUrl={iamApiUrl}
    />
    </>
  );
}

export default SignupModal;
